<template>
  <div>
    <!-- Header -->
    <div class="d-flex align-items-center justify-content-between mb-1">
      <div>
        <h4 class="font-weight-bolder mb-0">{{ isEditMode ? 'Edit Template' : 'Create New Template' }}</h4>
        <small class="text-muted">
          {{ isEditMode ? 'Edit the betting template configuration.' : 'Create a new betting template based on the' }}
          <strong v-if="!isEditMode">{{ baseTemplate }}</strong>
          <span v-if="!isEditMode"> template configuration.</span>
        </small>
      </div>
      <feather-icon
        icon="XIcon"
        size="20"
        class="cursor-pointer text-muted"
        @click="handleCancel"
      />
    </div>

    <!-- Template Name + Actions -->
    <b-card class="mb-1">
      <div class="d-flex align-items-center justify-content-between flex-wrap" style="gap: 8px">
        <div class="d-flex align-items-center" style="gap: 8px; flex: 1; max-width: 400px">
          <span class="font-weight-bold text-nowrap">Custom Template Name</span>
          <div class="d-flex align-items-center flex-grow-1">
            <span style="font-weight: 900;" class="mr-1 d-flex">{{ baseTemplate }}</span>
            <span class="mr-1">-</span>
            <b-form-input
              v-model="templateName"
              placeholder="Enter name..."
              size="sm"
            />
          </div>
        </div>
        <div class="d-flex" style="gap: 8px">
          <b-button size="sm" variant="outline-danger" @click="clearAllValues">
            <feather-icon icon="RefreshCwIcon" class="mr-50" />
            Clear All Values
          </b-button>
          <b-button size="sm" variant="outline-primary" @click="$refs.csvInput.click()">
            <feather-icon icon="UploadIcon" class="mr-50" />
            Upload CSV Template
          </b-button>
          <input ref="csvInput" type="file" accept=".csv" hidden @change="handleCSVUpload" />
        </div>
      </div>
    </b-card>

    <!-- Tabs Racing / Sports -->
    <b-card no-body>
      <b-tabs pills card>
        <b-tab active>
          <template #title><strong>Racing</strong></template>
          <div :key="renderKey">
            <div class="table-wrapper">
              <table class="text-sm text-left text-gray-900">
                <thead>
                  <SpreadsheetHeader />
                </thead>
                <tbody>
                  <SpreadsheetRow
                    v-for="row in generateRows()"
                    :key="row.key"
                    :row="row"
                    :config="row.data"
                    @onCellChange="handleCellChange"
                  />
                </tbody>
              </table>
            </div>
            <!-- Multi row -->
            <div class="mt-2">
              <table style="width: 100%">
                <tbody>
                  <tr>
                    <td class="bet-type-col sticky-col" style="background:#f8fafc; font-weight:600; font-size:12px; padding:12px 16px; min-width:100px">MULTI</td>
                    <td class="cell-col" style="padding:12px">
                      <div class="grid-9">
                        <EditCell
                          v-for="field in multiFields"
                          :key="field"
                          :value="getMultiVal(field)"
                          :type="field === 'accept' ? 'select' : 'input'"
                          :options="field === 'accept' ? ['Auto', 'Manual'] : []"
                          placeholder="0"
                          @changeInput="(val) => (multiItem[field] = val)"
                        />
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </b-tab>

        <b-tab>
          <template #title><strong>Sports</strong></template>
          <b-table
            :items="sportRows"
            :fields="sportFields"
            responsive
            bordered
            class="mt-1"
          >
            <template #head(league)>
              <div class="header-cell bg-light-cyan">Sports/Leagues</div>
            </template>
            <template #head(cashAmount)>
              <div class="header-cell bg-light-blue">CASH AMOUNT<br />(MAX STAKE)</div>
            </template>
            <template #head(bonusAmount)>
              <div class="header-cell bg-light-green">BONUS<br />(MAX STAKE)</div>
            </template>
            <template #head(cashLiability)>
              <div class="header-cell bg-light-purple">CASH<br />AMOUNT (LIABILITY)</div>
            </template>
            <template #head(bonusLiability)>
              <div class="header-cell bg-light-yellow">BONUS<br />AMOUNT (LIABILITY)</div>
            </template>
            <template #head(accept)>
              <div class="header-cell">ACCEPT</div>
            </template>
            <template #cell(league)="data">
              <strong>{{ data.item.league }}</strong>
            </template>
            <template v-for="f in sportNumericFields" v-slot:[`cell(${f})`]="data">
              <b-form-input
                size="sm"
                type="number"
                v-model="data.item[f]"
                :key="`${data.index}-${f}`"
                class="input-cell"
              />
            </template>
            <template #cell(accept)="data">
              <b-form-select
                :options="['Auto', 'Manual']"
                v-model="data.item.accept"
                size="sm"
                style="min-width: 90px"
              />
            </template>
          </b-table>
        </b-tab>
      </b-tabs>
    </b-card>

    <!-- Footer -->
    <div class="d-flex justify-content-end mt-1" style="gap: 8px">
      <b-button variant="outline-secondary" @click="handleCancel">Cancel</b-button>
      <b-button variant="primary" :disabled="!templateName.trim()" @click="handleCreate">
        {{ isEditMode ? 'Save Changes' : 'Create Template' }}
      </b-button>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BButton,
  BTabs,
  BTab,
  BFormInput,
  BFormSelect,
  BTable,
} from "bootstrap-vue";
import { mapActions, mapState } from "vuex";
import { parse } from "csv-parse";
import SpreadsheetHeader from "../TableRacing/SpreadsheetHeader.vue";
import SpreadsheetRow from "../TableRacing/SpreadsheetRow.vue";
import EditCell from "../TableRacing/EditCell.vue";

const INITIAL_RACING_BET_TYPES = [
  { betType: "WIN", key: "win" },
  { betType: "PLACE", key: "place" },
  { betType: "SP", key: "sp" },
  { betType: "BEST DIV SP", key: "best-div-sp" },
  { betType: "MID DIV WIN", key: "mid-div-win" },
  { betType: "MID DIV PLACE", key: "mid-div-place" },
  { betType: "EXACTA", key: "exacta" },
  { betType: "QUINELLA", key: "quinella" },
  { betType: "TRIFECTA", key: "trifecta" },
  { betType: "FIRST FOUR", key: "first4" },
  { betType: "SAME RACE MULTI", key: "srm" },
  { betType: "EARLY QUADDIE", key: "earlyQuaddie" },
  { betType: "QUADDIE", key: "lateQuaddie" },
  { betType: "EACH WAY FIXED", key: "each-way-fixed" },
  { betType: "EACH WAY BT+ MID", key: "each-way-best-div-mid-place" },
  { betType: "EACH WAY MID MID", key: "each-way-mid-div-mid-div-place" },
  { betType: "TOP K", key: "top-k" },
  { betType: "DAILY DOUBLE", key: "daily-double" },
  { betType: "RUNNING DOUBLE", key: "running-double" },
  { betType: "TREBLE", key: "treble" },
];

const BASE_INNER_CONFIG = {
  cashMaxStake: "",
  bonusMaxStake: "",
  maxCashLia: "",
  maxBonusLia: "",
  cashMaxStakeNoFixedOdds: "",
  bonusMaxStakeNoFixedOdds: "",
  maxCashLiaNoFixedOdds: "",
  maxBonusLiaNoFixedOdds: "",
  accept: "Auto",
};

const createDefaultInnerConfig = () => ({
  metroThoroughbreds: { ...BASE_INNER_CONFIG },
  nonMetroThoroughbreds: { ...BASE_INNER_CONFIG },
  internationalThoroughbreds: { ...BASE_INNER_CONFIG },
  metroGreyhounds: { ...BASE_INNER_CONFIG },
  nonMetroGreyhounds: { ...BASE_INNER_CONFIG },
  internationalGreyhounds: { ...BASE_INNER_CONFIG },
  metroHarness: { ...BASE_INNER_CONFIG },
  nonMetroHarness: { ...BASE_INNER_CONFIG },
  internationalHarness: { ...BASE_INNER_CONFIG },
});

export default {
  name: "CreateTemplate",
  components: {
    BCard,
    BButton,
    BTabs,
    BTab,
    BFormInput,
    BFormSelect,
    BTable,
    SpreadsheetHeader,
    SpreadsheetRow,
    EditCell,
  },
  data() {
    return {
      templateName: "",
      racingBetTypes: INITIAL_RACING_BET_TYPES.map((i) => ({
        ...i,
        data: createDefaultInnerConfig(),
      })),
      multiItem: { ...BASE_INNER_CONFIG },
      multiFields: [
        "cashMaxStake",
        "bonusMaxStake",
        "maxCashLia",
        "maxBonusLia",
        "cashMaxStakeNoFixedOdds",
        "bonusMaxStakeNoFixedOdds",
        "maxCashLiaNoFixedOdds",
        "maxBonusLiaNoFixedOdds",
        "accept",
      ],
      renderKey: 0,
      sportRows: [],
      sportFields: [
        { key: "league", label: "SPORTS/LEAGUES" },
        { key: "cashAmount" },
        { key: "bonusAmount" },
        { key: "cashLiability" },
        { key: "bonusLiability" },
        { key: "accept" },
      ],
      sportNumericFields: ["cashAmount", "bonusAmount", "cashLiability", "bonusLiability"],
    };
  },
  computed: {
    ...mapState("bettingConfig/list", ["activeProfile"]),
    baseTemplate() {
      return this.$route.query.baseTemplate || this.activeProfile || "";
    },
    isEditMode() {
      return !!this.$route.query.profileTypeId;
    },
    loadProfileTypeId() {
      return this.$route.query.profileTypeId || this.baseTemplate;
    },
  },
  async created() {
    this.templateName = this.$route.query.name || "";
    await this.loadRacingData();
    await this.loadSportData();
  },
  methods: {
    ...mapActions({
      getConfigRacingList: "bettingConfig/list/getConfigRacingList",
      getConfigSportList: "bettingConfig/list/getConfigSportList",
      getListSport: "sport/list/getListSport",
      upsetProfileRaceConfig: "bettingConfig/list/upsetProfileRaceConfig",
      upsetProfileSportConfig: "bettingConfig/list/upsetProfileSportConfig",
    }),

    async loadRacingData() {
      const res = await this.getConfigRacingList(this.loadProfileTypeId);
      if (!res || !res.length) return;

      const processed = INITIAL_RACING_BET_TYPES.map((i) => ({
        ...i,
        data: createDefaultInnerConfig(),
      }));

      res.forEach((item) => {
        const isCash = item.currencyMethod === "cash";
        const row = processed.find((i) => i.key === item.selectionType);
        if (row) {
          const convertKey = this.convertMeetingType(item.meetingType, item.jurisdiction);
          if (convertKey && row.data[convertKey]) {
            row.data[convertKey] = {
              ...row.data[convertKey],
              ...(isCash && { cashMaxStake: item.maxStake }),
              ...(!isCash && { bonusMaxStake: item.maxStake }),
              ...(isCash && { maxCashLia: item.maxLiability }),
              ...(!isCash && { maxBonusLia: item.maxLiability }),
              ...(isCash && { cashMaxStakeNoFixedOdds: item.maxStakeIfNoFixedOdds }),
              ...(!isCash && { bonusMaxStakeNoFixedOdds: item.maxStakeIfNoFixedOdds }),
              ...(isCash && { maxCashLiaNoFixedOdds: item.maxLiabilityIfNoFixedOdds }),
              ...(!isCash && { maxBonusLiaNoFixedOdds: item.maxLiabilityIfNoFixedOdds }),
              accept: item.autoAccept ? "Auto" : "Manual",
            };
          }
        }
        if (item.selectionType === "multi") {
          if (isCash) {
            this.multiItem = {
              ...this.multiItem,
              cashMaxStake: item.maxStake,
              maxCashLia: item.maxLiability,
              cashMaxStakeNoFixedOdds: item.maxStakeIfNoFixedOdds,
              maxCashLiaNoFixedOdds: item.maxLiabilityIfNoFixedOdds,
              accept: item.autoAccept ? "Auto" : "Manual",
            };
          } else {
            this.multiItem = {
              ...this.multiItem,
              bonusMaxStake: item.maxStake,
              maxBonusLia: item.maxLiability,
              bonusMaxStakeNoFixedOdds: item.maxStakeIfNoFixedOdds,
              maxBonusLiaNoFixedOdds: item.maxLiabilityIfNoFixedOdds,
            };
          }
        }
      });
      this.racingBetTypes = processed;
    },

    async loadSportData() {
      const data = await this.getListSport({ limit: 100, status: "active" });
      if (!data || !data.data) return;

      const baseRows = [
        ...data.data.map((sport) => ({
          league: sport.name,
          id: sport.id,
          cashAmount: 0,
          bonusAmount: 0,
          cashLiability: 0,
          bonusLiability: 0,
          accept: "Auto",
          tier: 1,
        })),
        { league: "Any Other Sport", id: "any-other-sport", cashAmount: 0, bonusAmount: 0, cashLiability: 0, bonusLiability: 0, accept: "Auto", tier: 1 },
        { league: "SGM", id: "sgm", cashAmount: 0, bonusAmount: 0, cashLiability: 0, bonusLiability: 0, accept: "Auto", tier: 1 },
        { league: "Cross Multi ANY Sport and racing", id: "cross-multi", cashAmount: 0, bonusAmount: 0, cashLiability: 0, bonusLiability: 0, accept: "Auto", tier: 1 },
      ];

      const res = await this.getConfigSportList(this.loadProfileTypeId);
      if (res && res.length) {
        this.sportRows = baseRows.map((row) => {
          const cash = res.find((i) => i.sportId === row.id && i.currencyMethod === "cash");
          const bonus = res.find((i) => i.sportId === row.id && i.currencyMethod === "bonus");
          return {
            ...row,
            cashAmount: cash ? cash.maxStake : row.cashAmount,
            cashLiability: cash ? cash.maxLiability : row.cashLiability,
            bonusAmount: bonus ? bonus.maxStake : row.bonusAmount,
            bonusLiability: bonus ? bonus.maxLiability : row.bonusLiability,
            accept: cash?.autoAccept || bonus?.autoAccept ? "Auto" : "Manual",
          };
        });
      } else {
        this.sportRows = baseRows;
      }
    },

    convertMeetingType(type, jurisdiction) {
      const j = jurisdiction.charAt(0).toLowerCase() + jurisdiction.slice(1);
      if (type === "R") return j + "Thoroughbreds";
      if (type === "G") return j + "Greyhounds";
      if (type === "H") return j + "Harness";
      return null;
    },

    generateRows() {
      return this.racingBetTypes.map(({ betType, key, data }) => ({
        betType,
        key,
        sectionKey: key,
        data,
      }));
    },

    handleCellChange(betTypeLocation, section, field, value) {
      const row = this.racingBetTypes.find((i) => i.key === betTypeLocation);
      if (row && row.data[section]) {
        row.data[section][field] = value;
      }
    },

    getMultiVal(field) {
      return this.multiItem[field] !== undefined ? `${this.multiItem[field]}` : "";
    },

    clearAllValues() {
      this.racingBetTypes = INITIAL_RACING_BET_TYPES.map((i) => ({
        ...i,
        data: createDefaultInnerConfig(),
      }));
      this.multiItem = { ...BASE_INNER_CONFIG };
      this.sportRows = this.sportRows.map((r) => ({
        ...r,
        cashAmount: 0,
        bonusAmount: 0,
        cashLiability: 0,
        bonusLiability: 0,
        accept: "Auto",
      }));
      this.renderKey += 1;
    },

    handleCSVUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => this.parseCSV(reader.result);
      reader.readAsText(file);
      this.$refs.csvInput.value = null;
    },

    parseCSV(csvData) {
      parse(csvData, { delimiter: "," }, (err, data) => {
        if (err) { console.error(err); return; }
        data.shift();
        this.racingBetTypes = INITIAL_RACING_BET_TYPES.map((i, index) => {
          const rowData = data[index + 1];
          if (!rowData) return { ...i, data: createDefaultInnerConfig() };
          return {
            ...i,
            data: {
              metroThoroughbreds: { cashMaxStake: rowData[1], bonusMaxStake: rowData[2], maxCashLia: rowData[3], maxBonusLia: rowData[4], cashMaxStakeNoFixedOdds: rowData[5], bonusMaxStakeNoFixedOdds: rowData[6], maxCashLiaNoFixedOdds: rowData[7], maxBonusLiaNoFixedOdds: rowData[8], accept: rowData[9] },
              nonMetroThoroughbreds: { cashMaxStake: rowData[10], bonusMaxStake: rowData[11], maxCashLia: rowData[12], maxBonusLia: rowData[13], cashMaxStakeNoFixedOdds: rowData[14], bonusMaxStakeNoFixedOdds: rowData[15], maxCashLiaNoFixedOdds: rowData[16], maxBonusLiaNoFixedOdds: rowData[17], accept: rowData[18] },
              internationalThoroughbreds: { cashMaxStake: rowData[19], bonusMaxStake: rowData[20], maxCashLia: rowData[21], maxBonusLia: rowData[22], cashMaxStakeNoFixedOdds: rowData[23], bonusMaxStakeNoFixedOdds: rowData[24], maxCashLiaNoFixedOdds: rowData[25], maxBonusLiaNoFixedOdds: rowData[26], accept: rowData[27] },
              metroGreyhounds: { cashMaxStake: rowData[28], bonusMaxStake: rowData[29], maxCashLia: rowData[30], maxBonusLia: rowData[31], cashMaxStakeNoFixedOdds: rowData[32], bonusMaxStakeNoFixedOdds: rowData[33], maxCashLiaNoFixedOdds: rowData[34], maxBonusLiaNoFixedOdds: rowData[35], accept: rowData[36] },
              nonMetroGreyhounds: { cashMaxStake: rowData[37], bonusMaxStake: rowData[38], maxCashLia: rowData[39], maxBonusLia: rowData[40], cashMaxStakeNoFixedOdds: rowData[41], bonusMaxStakeNoFixedOdds: rowData[42], maxCashLiaNoFixedOdds: rowData[43], maxBonusLiaNoFixedOdds: rowData[44], accept: rowData[45] },
              internationalGreyhounds: { cashMaxStake: rowData[46], bonusMaxStake: rowData[47], maxCashLia: rowData[48], maxBonusLia: rowData[49], cashMaxStakeNoFixedOdds: rowData[50], bonusMaxStakeNoFixedOdds: rowData[51], maxCashLiaNoFixedOdds: rowData[52], maxBonusLiaNoFixedOdds: rowData[53], accept: rowData[54] },
              metroHarness: { cashMaxStake: rowData[55], bonusMaxStake: rowData[56], maxCashLia: rowData[57], maxBonusLia: rowData[58], cashMaxStakeNoFixedOdds: rowData[59], bonusMaxStakeNoFixedOdds: rowData[60], maxCashLiaNoFixedOdds: rowData[61], maxBonusLiaNoFixedOdds: rowData[62], accept: rowData[63] },
              nonMetroHarness: { cashMaxStake: rowData[64], bonusMaxStake: rowData[65], maxCashLia: rowData[66], maxBonusLia: rowData[67], cashMaxStakeNoFixedOdds: rowData[68], bonusMaxStakeNoFixedOdds: rowData[69], maxCashLiaNoFixedOdds: rowData[70], maxBonusLiaNoFixedOdds: rowData[71], accept: rowData[72] },
              internationalHarness: { cashMaxStake: rowData[73], bonusMaxStake: rowData[74], maxCashLia: rowData[75], maxBonusLia: rowData[76], cashMaxStakeNoFixedOdds: rowData[77], bonusMaxStakeNoFixedOdds: rowData[78], maxCashLiaNoFixedOdds: rowData[79], maxBonusLiaNoFixedOdds: rowData[80], accept: rowData[81] },
            },
          };
        });
        this.renderKey += 1;
      });
    },

    getMapping(meetingKey) {
      let meetingType = "";
      if (meetingKey.includes("Thoroughbreds")) meetingType = "R";
      else if (meetingKey.includes("Greyhounds")) meetingType = "G";
      else if (meetingKey.includes("Harness")) meetingType = "H";

      let jurisdiction = "";
      if (meetingKey.includes("international")) jurisdiction = "International";
      else if (meetingKey.includes("nonMetro")) jurisdiction = "NonMetro";
      else if (meetingKey.includes("metro")) jurisdiction = "Metro";

      return meetingType && jurisdiction ? { meetingType, jurisdiction } : null;
    },

    handleCancel() {
      this.$router.back();
    },

    async handleCreate() {
      const profileTypeId = `${this.baseTemplate}-${this.templateName.trim()}`;
      const tag = this.baseTemplate;

      // --- Racing configs ---
      const racingConfigs = [];
      this.racingBetTypes.forEach((betType) => {
        Object.keys(betType.data).forEach((meetingKey) => {
          const d = betType.data[meetingKey];
          const mapping = this.getMapping(meetingKey);
          if (!mapping) return;

          const base = {
            meetingType: mapping.meetingType,
            jurisdiction: mapping.jurisdiction,
            selectionType: betType.key,
            autoAccept: d.accept === "Auto",
          };
          racingConfigs.push({
            ...base,
            currencyMethod: "cash",
            maxStake: Number(d.cashMaxStake) || 0,
            maxLiability: Number(d.maxCashLia) || 0,
            maxStakeIfNoFixedOdds: Number(d.cashMaxStakeNoFixedOdds) || 0,
            maxLiabilityIfNoFixedOdds: Number(d.maxCashLiaNoFixedOdds) || 0,
          });
          racingConfigs.push({
            ...base,
            currencyMethod: "bonus",
            maxStake: Number(d.bonusMaxStake) || 0,
            maxLiability: Number(d.maxBonusLia) || 0,
            maxStakeIfNoFixedOdds: Number(d.bonusMaxStakeNoFixedOdds) || 0,
            maxLiabilityIfNoFixedOdds: Number(d.maxBonusLiaNoFixedOdds) || 0,
          });
        });
      });

      // Multi rows
      racingConfigs.push({
        selectionType: "multi",
        currencyMethod: "cash",
        autoAccept: this.multiItem.accept === "Auto",
        maxStake: Number(this.multiItem.cashMaxStake) || 0,
        maxLiability: Number(this.multiItem.maxCashLia) || 0,
        maxStakeIfNoFixedOdds: Number(this.multiItem.cashMaxStakeNoFixedOdds) || 0,
        maxLiabilityIfNoFixedOdds: Number(this.multiItem.maxCashLiaNoFixedOdds) || 0,
      });
      racingConfigs.push({
        selectionType: "multi",
        currencyMethod: "bonus",
        autoAccept: this.multiItem.accept === "Auto",
        maxStake: Number(this.multiItem.bonusMaxStake) || 0,
        maxLiability: Number(this.multiItem.maxBonusLia) || 0,
        maxStakeIfNoFixedOdds: Number(this.multiItem.bonusMaxStakeNoFixedOdds) || 0,
        maxLiabilityIfNoFixedOdds: Number(this.multiItem.maxBonusLiaNoFixedOdds) || 0,
      });

      // --- Sport configs ---
      const sportConfigs = [];
      this.sportRows.forEach((item) => {
        sportConfigs.push({
          sportId: item.id,
          tier: item.tier || 1,
          currencyMethod: "cash",
          maxStake: Number(item.cashAmount) || 0,
          maxLiability: Number(item.cashLiability) || 0,
          autoAccept: item.accept === "Auto",
        });
        sportConfigs.push({
          sportId: item.id,
          tier: item.tier || 1,
          currencyMethod: "bonus",
          maxStake: Number(item.bonusAmount) || 0,
          maxLiability: Number(item.bonusLiability) || 0,
          autoAccept: item.accept === "Auto",
        });
      });

      const [raceRes, sportRes] = await Promise.all([
        this.upsetProfileRaceConfig({ profileTypeId, tag, configs: racingConfigs }),
        this.upsetProfileSportConfig({ profileTypeId, tag, configs: sportConfigs }),
      ]);

      if (raceRes?.data && sportRes?.data) {
        this.$router.back();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.table-wrapper {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.bet-type-col {
  padding: 12px 16px;
  font-weight: 600;
  background: #f8fafc;
  font-size: 12px;
  position: sticky;
  left: 0;
  z-index: 10;
  min-width: 100px;
}

.grid-9 {
  display: grid;
  grid-template-columns: repeat(9, 1fr);
  gap: 16px;
}

.input-cell {
  text-align: center;
  border-radius: 6px;
  font-size: 14px;
}

.header-cell {
  text-align: center;
  font-weight: bold;
  display: flex;
  padding: 8px;
  height: 60px;
  align-items: center;
  justify-content: center;
}

.bg-light-cyan { background: #cefafe !important; }
.bg-light-blue { background: #d6f0ff; }
.bg-light-green { background: #e5ffe5; }
.bg-light-purple { background: #f2e5ff; }
.bg-light-yellow { background: #fff8d6; }
</style>
